import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Store, Wallet, IndianRupee, Pencil, AlertTriangle, History, Trash2 } from "lucide-react";
import { listSalaries, setEmployeeSalary, getEmployeeSalary, deleteSalaryRevision } from "../../api/payroll";
import { listBranches } from "../../api/branches";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Modal } from "../../components/Modal";
import {
  Card, Button, Input, Select, EmptyState, TableSkeleton, StatCard, Skeleton, Avatar, Field, ConfirmDialog,
} from "../../components/ui";
import { unwrapList, errorMessage, fieldErrors, formatDate, todayISO } from "../../lib/records";
import { formatMoney } from "../../lib/salary";

// §11.3 — one row per employee, including those with no salary yet
// (`salarySet: false`), so nobody is forgotten before payday.
export default function SalariesList() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";

  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((res) => setBranches(unwrapList(res, 100).items))
      .catch(() => setBranches([]));
  }, [isChain]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await listSalaries({ ...(isChain && branchId ? { partner: branchId } : {}) });
      const body = res?.data ?? {};
      setRows(body.items ?? (Array.isArray(body) ? body : []));
      setTotals(body.totals ?? null);
    } catch (err) {
      setRows([]);
      setTotals(null);
      setLoadError(errorMessage(err, "Salaries could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [branchId, isChain]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const unsetCount = rows.filter((r) => !r.salarySet).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <Link
          to="/payroll"
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors"
        >
          <ArrowLeft size={15} /> Back to payroll
        </Link>

        <PageHeader
          title="Salaries"
          subtitle="The decided monthly pay each month is priced from. Changes are kept as revisions."
          actions={
            isChain && (
              <div className="flex items-center gap-2">
                <Store size={15} className="shrink-0 text-gray-400" />
                <Select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className="w-full sm:w-56 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                >
                  <option value="">All branches</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </div>
            )
          }
        />

        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => (
              <Card key={i} className="p-3 sm:p-5 bg-white/80 backdrop-blur-sm border border-emerald-100">
                <Skeleton className="h-3 sm:h-4 w-24" />
                <Skeleton className="mt-2 sm:mt-3 h-6 sm:h-8 w-16" />
              </Card>
            ))
          ) : (
            <>
              <StatCard
                label="Monthly wage bill"
                value={formatMoney(totals?.monthlyWageBill ?? 0)}
                icon={Wallet}
                tone="primary"
              />
              <StatCard
                label="Salary not set"
                value={unsetCount}
                icon={AlertTriangle}
                tone={unsetCount > 0 ? "orange" : "green"}
              />
            </>
          )}
        </div>

        {loadError && !loading && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
            {loadError}
          </div>
        )}

        <Card className="mt-4 sm:mt-5 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 overflow-hidden">
          {loading ? (
            <TableSkeleton rows={6} cols={4} />
          ) : rows.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={Wallet}
                title="No employees yet"
                description="Add staff and their salaries will be managed here."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="text-xs sm:text-label-sm uppercase tracking-wide text-gray-500 border-b border-emerald-100">
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Employee</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold">Monthly salary</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 font-semibold hidden sm:table-cell">In force since</th>
                    <th className="px-3 sm:px-4 py-2 sm:py-3 text-right w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-emerald-100/50">
                  {rows.map((row) => (
                    <tr
                      key={row.employee?._id ?? row.employee}
                      className="text-xs sm:text-body-sm text-gray-800 hover:bg-emerald-50/50 transition-colors duration-200"
                    >
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        <Link to={`/employees/${row.employee?._id}`} className="flex items-center gap-2 group/emp">
                          <Avatar name={row.employee?.name ?? "?"} size={28} />
                          <div className="min-w-0">
                            <p className="truncate text-xs font-medium text-gray-800 group-hover/emp:text-emerald-700 transition-colors">
                              {row.employee?.name ?? "Unknown"}
                            </p>
                            {row.employee?.designation && (
                              <p className="truncate text-xs text-gray-500">{row.employee.designation}</p>
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        {row.salarySet ? (
                          <span className="font-semibold text-gray-800">{formatMoney(row.monthlyAmount)}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-label-sm font-semibold text-amber-800">
                            <AlertTriangle size={12} /> Not set
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 hidden sm:table-cell text-gray-500">
                        {row.effectiveFrom ? formatDate(row.effectiveFrom) : "—"}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-right">
                        <Button
                          size="sm"
                          variant={row.salarySet ? "outline" : "primary"}
                          onClick={() => setEditing(row)}
                          className={
                            row.salarySet
                              ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs px-2.5 py-1"
                              : "bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs px-2.5 py-1"
                          }
                        >
                          <Pencil size={13} /> {row.salarySet ? "Revise" : "Set"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="mt-4 text-xs text-gray-500">
          Revisions are kept, never overwritten. Back-date <strong>effective from</strong> to correct an
          old month, or forward-date it to schedule a raise — each month is priced with the revision in
          force at that month's end.
        </p>

        {editing && (
          <SalaryModal
            open
            row={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              fetchRows();
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * §11.1 / §11.2 — set a salary and review the revision history.
 *
 * Re-posting the same `effectiveFrom` corrects that revision rather than
 * stacking a duplicate, which is what makes this safe to use for fixes.
 */
function SalaryModal({ open, onClose, row, onSaved }) {
  const employeeId = row.employee?._id ?? row.employee;

  const [amount, setAmount] = useState(row.salarySet ? String(row.monthlyAmount) : "");
  const [effectiveFrom, setEffectiveFrom] = useState(todayISO());
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [revisionToDelete, setRevisionToDelete] = useState(null);
  const [deletingRevision, setDeletingRevision] = useState(false);

  const loadDetail = useCallback(() => {
    setDetailLoading(true);
    getEmployeeSalary(employeeId)
      .then((res) => setDetail(res?.data ?? null))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [employeeId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  async function submit(e) {
    e.preventDefault();
    setErrors({});
    setFormError("");
    setSaving(true);
    try {
      await setEmployeeSalary(employeeId, {
        monthlyAmount: Number(amount),
        ...(effectiveFrom ? { effectiveFrom } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onSaved();
    } catch (err) {
      const mapped = fieldErrors(err);
      if (mapped) setErrors(mapped);
      else setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDeleteRevision() {
    setDeletingRevision(true);
    try {
      await deleteSalaryRevision(employeeId, revisionToDelete._id);
      setRevisionToDelete(null);
      loadDetail();
    } catch (err) {
      setFormError(errorMessage(err, "That revision could not be removed."));
    } finally {
      setDeletingRevision(false);
    }
  }

  const history = detail?.history ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={row.salarySet ? "Revise salary" : "Set salary"}
      description={row.employee?.name ?? "Employee"}
    >
      <form onSubmit={submit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        {detail?.current && (
          <div className="flex items-center justify-between rounded-md bg-emerald-50/60 px-3.5 py-2.5">
            <span className="text-sm text-gray-600">Currently</span>
            <span className="text-sm font-semibold text-gray-800">
              {formatMoney(detail.current.monthlyAmount)}
              <span className="ml-1.5 font-normal text-gray-500">
                since {formatDate(detail.current.effectiveFrom)}
              </span>
            </span>
          </div>
        )}

        {detail?.upcoming && (
          <div className="flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-3.5 py-2.5">
            <span className="text-sm text-blue-800">Scheduled</span>
            <span className="text-sm font-semibold text-blue-900">
              {formatMoney(detail.upcoming.monthlyAmount)}
              <span className="ml-1.5 font-normal">from {formatDate(detail.upcoming.effectiveFrom)}</span>
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          <Field label="Monthly amount" required error={errors.monthlyAmount}>
            <Input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
              inputMode="decimal"
              placeholder="30000"
              required
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
          <Field
            label="Effective from"
            error={errors.effectiveFrom}
            hint="Back-date to fix a past month, forward-date to schedule a raise."
          >
            <Input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>
        </div>

        <Field label="Note" error={errors.note}>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Annual increment"
            className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
          />
        </Field>

        {/* Revision history — the reason back-dating is safe is visible here. */}
        <div className="rounded-md border border-emerald-100 bg-emerald-50/30 p-3">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-gray-700">
            <History size={13} className="text-emerald-600" /> Revision history
          </p>
          {detailLoading ? (
            <Skeleton className="h-12" />
          ) : history.length === 0 ? (
            <p className="text-xs text-gray-500">No revisions yet — this will be the first.</p>
          ) : (
            <ul className="max-h-36 space-y-1 overflow-y-auto">
              {history.map((rev) => (
                <li
                  key={rev._id}
                  className="flex items-center justify-between rounded bg-white/70 px-2.5 py-1.5 text-xs"
                >
                  <span className="text-gray-700">
                    <strong>{formatMoney(rev.monthlyAmount)}</strong>
                    <span className="text-gray-500"> from {formatDate(rev.effectiveFrom)}</span>
                    {rev.note && <span className="text-gray-400"> · {rev.note}</span>}
                  </span>
                  <button
                    type="button"
                    onClick={() => setRevisionToDelete(rev)}
                    title="Remove this revision"
                    className="ml-2 shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} className="text-gray-600 w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
          >
            <IndianRupee size={15} /> Save revision
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={!!revisionToDelete}
        onClose={() => setRevisionToDelete(null)}
        onConfirm={confirmDeleteRevision}
        loading={deletingRevision}
        danger
        title="Remove this revision?"
        description={
          revisionToDelete
            ? `${formatMoney(revisionToDelete.monthlyAmount)} from ${formatDate(revisionToDelete.effectiveFrom)} will be removed. Months priced on it will be repriced with whichever revision then applies.`
            : ""
        }
        confirmLabel="Remove"
      />
    </Modal>
  );
}
